import { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowRight, Loader, Coins, AlertCircle, RotateCcw } from 'lucide-react';
import { Button, Card } from '@/components/ui';
import { Logo } from '@/components/layout';
import { useAuth } from '@/contexts/AuthContext';
import { useCredits } from '@/hooks/useCredits';
import { api } from '@/lib/api';

export function CreditPurchaseSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { balance, isUnlimited, fetchBalance } = useCredits();
  // sessionId available for future verification use
  const _sessionId = searchParams.get('session_id');
  void _sessionId;
  const purchaseId = searchParams.get('purchase_id');
  const [isVerifying, setIsVerifying] = useState(true);
  const [verificationError, setVerificationError] = useState(false);
  const pollingStartedRef = useRef(false);

  // Poll for purchase completion by checking the purchase record status
  const pollForPurchaseCompletion = useCallback(async () => {
    const maxAttempts = 8;
    const delayMs = 2000;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      if (attempt > 0) {
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }

      try {
        if (purchaseId) {
          const result = await api.getCreditPurchaseStatus(purchaseId);
          if (result.status === 'completed') {
            // Credits confirmed — refresh balance and show success
            await fetchBalance();
            setIsVerifying(false);
            return;
          }
          if (result.status === 'failed') {
            setVerificationError(true);
            setIsVerifying(false);
            return;
          }
        } else {
          // No purchase_id — fall back to just refreshing balance
          await fetchBalance();
        }
      } catch {
        // API error — continue polling
      }
    }

    // All attempts exhausted — webhook hasn't processed yet
    await fetchBalance();
    setVerificationError(true);
    setIsVerifying(false);
  }, [purchaseId, fetchBalance]);

  useEffect(() => {
    if (authLoading) return;

    if (!isAuthenticated) {
      navigate('/login', {
        replace: true,
        state: { from: { pathname: '/dashboard' } }
      });
      return;
    }

    // Prevent concurrent polling loops
    if (pollingStartedRef.current) return;
    pollingStartedRef.current = true;

    pollForPurchaseCompletion();
  }, [authLoading, isAuthenticated, navigate, pollForPurchaseCompletion]);

  // Handle manual retry
  const handleRetry = async () => {
    setIsVerifying(true);
    setVerificationError(false);
    pollingStartedRef.current = false;
    await pollForPurchaseCompletion();
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="border-b border-border">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Logo size="md" linkTo="/" />
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <Card className="max-w-md w-full text-center p-8">
          {isVerifying ? (
            <>
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                <Loader className="w-12 h-12 text-primary animate-spin" />
              </div>
              <h1 className="text-2xl font-bold mb-2">Processing Purchase...</h1>
              <p className="text-text-secondary mb-2">
                Please wait while we confirm your credit purchase.
              </p>
            </>
          ) : verificationError ? (
            <>
              <div className="w-16 h-16 bg-warning/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-10 h-10 text-warning" />
              </div>
              <h1 className="text-2xl font-bold mb-2">Verification Pending</h1>
              <p className="text-text-secondary mb-4">
                Your payment was received but credits are still being processed.
                This usually takes a few moments.
              </p>
              <div className="space-y-3">
                <Button
                  variant="primary"
                  className="w-full"
                  onClick={handleRetry}
                  leftIcon={<RotateCcw className="w-4 h-4" />}
                >
                  Check Again
                </Button>
                <Link to="/dashboard" className="block">
                  <Button variant="secondary" className="w-full">
                    Go to Dashboard
                  </Button>
                </Link>
              </div>
            </>
          ) : (
            <>
              <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-success" />
              </div>
              <h1 className="text-2xl font-bold mb-2">Purchase Successful!</h1>
              <p className="text-text-secondary mb-4">
                Your credits have been added to your account.
              </p>

              {/* Show current balance */}
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-center gap-2 text-primary mb-2">
                  <Coins className="w-5 h-5" />
                  <span className="font-medium">Current Balance</span>
                </div>
                <p className="text-2xl font-bold text-primary">
                  {isUnlimited
                    ? 'Unlimited'
                    : balance !== null
                      ? `${balance.toLocaleString()} credits`
                      : '...'
                  }
                </p>
              </div>

              <div className="space-y-3">
                <Link to="/dashboard" className="block">
                  <Button variant="primary" className="w-full" rightIcon={<ArrowRight className="w-4 h-4" />}>
                    Go to Dashboard
                  </Button>
                </Link>
                <Link to="/research" className="block">
                  <Button variant="secondary" className="w-full">
                    Start Research
                  </Button>
                </Link>
              </div>
            </>
          )}
        </Card>
      </main>

      {/* Help Section */}
      <section className="border-t border-border py-8 px-4">
        <div className="max-w-md mx-auto text-center">
          <h2 className="font-semibold mb-3">Need Help?</h2>
          <p className="text-text-secondary text-sm mb-4">
            If your credits don't appear within a few minutes, please contact support
            with your purchase ID: <code className="text-xs bg-surface-light px-1 py-0.5 rounded">{purchaseId || 'N/A'}</code>
          </p>
          <a href="mailto:contact@vettis.ai" className="text-primary hover:underline text-sm">
            Contact Support
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-6 px-4 text-center">
        <p className="text-text-muted text-sm">
          &copy; {new Date().getFullYear()} Vettis. All rights reserved.
        </p>
      </footer>
    </div>
  );
}

